import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Spinner } from 'react-bootstrap';
import { useToast } from '../../context/ToastContext';
import { dateToUTC } from '../../utils/dateUtils';
import { useGetAllGalleriesQuery } from '../../services/endpoints/website/gallery';
import { useGetAllPagesQuery } from '../../services/endpoints/website/page';
import { GalleryImage, Page } from '../../types/website.types';
import { BackButton } from '../../components/buttons/BackButton';
import ImageWithErrorHandling from '../../components/image/ImageWithErrorHandling';
import { getPartition } from '../../utils/utils';
import './StickyNotes.css';

function prepareStickyNoteContent(html: string): string {
    let content = html
        .replace(/color:\s*#ffffff\b/gi, 'color:#212529')
        .replace(/color:\s*#fff\b/gi, 'color:#212529')
        .replace(/color:\s*white\b/gi, 'color:#212529');

    // Drop leading empty paragraphs often left by the CMS editor
    content = content.replace(/^(\s*<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>)+/i, '');

    return content.trim();
}

export const StickyNoteDetails = () => {
    const params = useParams();
    const navigate = useNavigate();
    const { addToast } = useToast();
    const [page, setPage] = useState<Page | undefined>();
    const slug = params.slug;

    const { data, error, isLoading } = useGetAllPagesQuery(
        slug
            ? {
                  data: {
                      filterBy: [{ operator: 'equals', field: 'slug', value: slug }],
                      entries: 1,
                      page: 1
                  }
              }
            : undefined,
        { skip: !slug }
    );

    const { data: galleryData } = useGetAllGalleriesQuery(
        page?.gallery_id
            ? {
                  data: {
                      filterBy: [{ operator: 'equals', field: 'id', value: page.gallery_id }],
                      entries: 1,
                      page: 1
                  }
              }
            : undefined,
        { skip: !page?.gallery_id }
    );

    const galleryPictures: GalleryImage[] =
        galleryData?.results?.[0]?.pictures?.filter((pic: GalleryImage) => pic.image_file_name) ?? [];

    useEffect(() => {
        if (error && !isLoading) {
            addToast('Error while loading sticky note', 'error');
        }
    }, [error, isLoading, addToast]);

    useEffect(() => {
        if (!slug) {
            navigate('/sticky-notes');
            return;
        }

        if (data?.results) {
            const foundPage = data.results[0];
            if (foundPage) {
                setPage(foundPage);
            } else if (!isLoading) {
                addToast('Sticky note not found', 'error');
                navigate('/sticky-notes');
            }
        }
    }, [data, slug, isLoading, navigate, addToast]);

    if (isLoading || !page) {
        return (
            <div className="w-100 vh-100 d-inline-flex">
                <Spinner variant="primary" className="m-auto" />
            </div>
        );
    }

    return (
        <div id="sticky-note-details">
            {/* Header with back button */}
            <div className="w-100 header-image-sticky-notes sticky-note-details-header">
                <Container>
                    <BackButton text="Back to Sticky Notes" color="#FFFFFF" size="1rem" />
                </Container>
            </div>

            <Container fluid className="bg-white text-blue">
                <Container className="py-5 sticky-note-body">
                    {/* Article Header */}
                    <div className="sticky-note-article-header">
                        <h1 className="text-weight-700 text-blue sticky-note-title">{page.name}</h1>

                        {/* Meta information */}
                        <div className="d-flex flex-wrap gap-3 sticky-note-meta" style={{ color: '#888888' }}>
                            {page.author && (
                                <span>
                  <strong>By:</strong> {page.author}
                </span>
                            )}
                            {page.published_on && (
                                <span>
                  <strong>Published:</strong> {dateToUTC(new Date(page.published_on), 'MMMM dd, yyyy')}
                </span>
                            )}
                        </div>
                    </div>

                    {page.image_file_name && (
                        <div className="sticky-note-featured-image-container">
                            <ImageWithErrorHandling
                                src={`${page?.s3_base_url}/${getPartition(page?.id)}/medium/${page?.image_file_name}`}
                                alt={page.name}
                                className="sticky-note-featured-image"
                            />
                        </div>
                    )}

                    {/* Article Content */}
                    <div
                        className="sticky-note-content"
                        dangerouslySetInnerHTML={{ __html: prepareStickyNoteContent(page.content) }}
                        style={{
                            fontSize: '1.1rem',
                            lineHeight: '1.8',
                            color: '#212529'
                        }}
                    />

                    {/* Call to Action Button */}
                    {page.button_text && page.button_target && (
                        <div className="mt-5 text-center">
                            <a
                                href={page.button_target}
                                target={page.button_opens_in_new_window ? '_blank' : '_self'}
                                rel={page.button_opens_in_new_window ? 'noopener noreferrer' : undefined}
                                className="btn btn-primary btn-lg"
                                style={{
                                    backgroundColor: '#B4C634',
                                    borderColor: '#B4C634',
                                    padding: '12px 40px',
                                    fontSize: '1.2rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                {page.button_text}
                            </a>
                        </div>
                    )}

                    {galleryPictures.length > 0 && (
                        <div className="sticky-note-gallery mt-5">
                            <div className="photo-grid">
                                {galleryPictures.map((pic: GalleryImage, i: number) => (
                                    <div key={pic.id ?? i} className="photo-grid-item">
                                        <img
                                            src={`${pic.s3_base_url}/${getPartition(pic.id)}/original/${pic.image_file_name}`}
                                            alt={pic.name ?? `${page.name} photo ${i + 1}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Back to Sticky Notes */}
                    <div className="mt-5 pt-4 border-top">
                        <BackButton text="← Back to All Sticky Notes" color="#2B4E64" size="1rem" />
                    </div>
                </Container>
            </Container>
        </div>
    );
};